import { createContext, useContext } from 'react';
import type { AccountInScopeDto } from '@xcentral/api-client';

/**
 * BT001 #16306 — the client-portal account context.
 *
 * The active account lives in the URL (`?account={guid}`), never in a store.
 * `localStorage` is shared across tabs, so switching in one tab would silently
 * change the others; `sessionStorage` is *inherited by a duplicated tab*, which
 * is exactly how a partner admin opens the second account. The URL is the only
 * carrier that is per-tab by construction, survives refresh, and makes every
 * client read reproducible from its address (BR-ACTX-003).
 *
 * The id in the URL is a REQUEST, never an authority (BR-ACTX-006): every read
 * still sends it to an endpoint that validates it server-side against the
 * caller's parent-child scope (`ResolveCallerInScopeAsync`). The client-side
 * drop-back below is a convenience that avoids a guaranteed-403 round trip —
 * it is never the control.
 */
export interface AccountContextValue {
  /**
   * The account this tab is working in. `null` while unresolved, and when the
   * caller has no PortalUser profile at all — consumers must not fetch until
   * this is a non-null id (that is what keeps an out-of-scope `?account` from
   * ever reaching the wire, AC-5).
   */
  activeAccountId: string | null;
  /** Display name of the active account. Never the GUID, AccountNumber, or any commercial field (BR-HW-003). */
  activeAccountName: string;
  /** The caller's OWN account — the fallback target and the switcher's first entry. */
  ownAccountId: string | null;
  /** Display name of the caller's own account. */
  ownAccountName: string;
  /** Own account + direct children, one level, in the server's order. Empty for non-ClientAdmin callers (403). */
  accountsInScope: AccountInScopeDto[];
  /** True only when the caller's own account has at least one direct child. Gates the switcher entirely (BR-ACTX-002). */
  isParent: boolean;
  /** True until the scope read settles. Consumers should hold their loading state while set. */
  loading: boolean;
  /** Navigate this tab to another in-scope account. Writes no store of any kind. */
  switchTo: (accountId: string) => void;
}

export const DEFAULT_ACCOUNT_CONTEXT: AccountContextValue = {
  activeAccountId: null,
  activeAccountName: '',
  ownAccountId: null,
  ownAccountName: '',
  accountsInScope: [],
  isParent: false,
  loading: false,
  switchTo: () => {},
};

export const AccountContext = createContext<AccountContextValue>(DEFAULT_ACCOUNT_CONTEXT);

/**
 * The single hook every client-portal screen adopts to learn which account it
 * is rendering. Resolves from the URL, falling back to the caller's own account.
 */
export function useAccountContext(): AccountContextValue {
  return useContext(AccountContext);
}

/** The query-parameter name that carries the account. Exported so tests and callers never hardcode it. */
export const ACCOUNT_QUERY_PARAM = 'account';
